import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';

export interface TabDefinition {
  id: string;
  label?: string;
  labelKey?: string;
  icon?: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
}

export interface TabbedComponentProps {
  tabs: TabDefinition[];
  activeTabId?: string;
  defaultActiveTabId?: string;
  onTabChange?: (tabId: string) => void;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
  style?: React.CSSProperties; // FIX: Added style prop
  tabListClassName?: string;
  tabButtonClassName?: string;
  activeTabButtonClassName?: string;
  inactiveTabButtonClassName?: string;
  tabPanelContainerClassName?: string;
  applyNeobrutalistEffectsToTabs?: boolean;
  applyCustomScrollbarToPanel?: boolean;
  containerClassName?: string; // Prop for the outermost div
  contentClassName?: string; // Prop for the content div inside the panel
}

export const TabbedComponent: React.FC<TabbedComponentProps> = ({
  tabs,
  activeTabId: controlledActiveTabId,
  defaultActiveTabId,
  onTabChange,
  style,
  orientation = 'horizontal',
  className,
  tabListClassName,
  tabButtonClassName,
  activeTabButtonClassName,
  inactiveTabButtonClassName,
  tabPanelContainerClassName,
  applyNeobrutalistEffectsToTabs = true,
  applyCustomScrollbarToPanel = false,
  containerClassName,
  contentClassName,
}) => {
  const { t } = useTranslation();
  const [internalActiveTabId, setInternalActiveTabId] = useState(
    defaultActiveTabId || (tabs.length > 0 ? tabs.find(tab => !tab.disabled)?.id : undefined)
  );

  const activeTab = controlledActiveTabId !== undefined ? controlledActiveTabId : internalActiveTabId;

  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (controlledActiveTabId !== undefined && controlledActiveTabId !== internalActiveTabId) {
        setInternalActiveTabId(controlledActiveTabId);
    } else if (tabs.length > 0 && activeTab && !tabs.find(tab => tab.id === activeTab && !tab.disabled)) {
        setInternalActiveTabId(tabs.find(tab => !tab.disabled)?.id);
    } else if (tabs.length > 0 && !activeTab) { 
        setInternalActiveTabId(tabs.find(tab => !tab.disabled)?.id);
    }
  }, [controlledActiveTabId, tabs, activeTab, internalActiveTabId]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let newIndex = index;
    const isHorizontalNav = orientation === 'horizontal';
    
    if ((isHorizontalNav && (event.key === 'ArrowRight' || event.key === 'ArrowDown')) ||
        (!isHorizontalNav && event.key === 'ArrowDown')) {
      newIndex = (index + 1);
    } else if ((isHorizontalNav && (event.key === 'ArrowLeft' || event.key === 'ArrowUp')) ||
               (!isHorizontalNav && event.key === 'ArrowUp')) {
      newIndex = (index - 1);
    } else if (event.key === 'Home') {
      newIndex = 0;
    } else if (event.key === 'End') {
      newIndex = tabs.length - 1;
    } else if (event.key === 'Enter' || event.key === ' ') {
      if (!tabs[index].disabled) {
        handleTabClick(tabs[index].id);
      }
      event.preventDefault();
      return;
    } else {
      return; 
    }
    event.preventDefault();

    const nextFocusableIndex = newIndex;
    let direction = newIndex > index ? 1 : -1;
    if (event.key === 'Home') direction = 1; 
    if (event.key === 'End') direction = -1; 
    
    for (let i = 0; i < tabs.length; i++) {
        const checkIndex = (nextFocusableIndex + i * direction + tabs.length) % tabs.length;
        if (!tabs[checkIndex]?.disabled) {
            tabRefs.current[checkIndex]?.focus();
            return;
        }
    }
  };

  const handleTabClick = (tabId: string) => {
    const clickedTab = tabs.find(t => t.id === tabId);
    if(clickedTab?.disabled) return;

    if (controlledActiveTabId === undefined) {
      setInternalActiveTabId(tabId);
    }
    onTabChange?.(tabId);
  };

  const activeContent = tabs.find((tab) => tab.id === activeTab)?.content;
  const isHorizontal = orientation === 'horizontal';

  const scrollbarClasses = applyCustomScrollbarToPanel
      ? 'scrollbar scrollbar-thin scrollbar-thumb-black scrollbar-track-slate-100 scrollbar-thumb-rounded-full scrollbar-track-rounded-full'
      : '';

  return (
    <div className={clsx(
        'flex bg-white rounded-xl border-2 border-black overflow-hidden',
        '[.theme-liquid-glass_&]:bg-white/5 [.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20',
        '[.theme-liquid-glass_&]:rounded-3xl [.theme-liquid-glass_&]:backdrop-blur-xl',
        isHorizontal ? 'flex-col' : 'flex-row min-h-[350px]',
        className,
        containerClassName // Apply prop here
    )} style={style}>
      {/* Tab List */}
      <div
        role="tablist"
        aria-orientation={orientation}
        className={clsx(
          'flex shrink-0 bg-slate-50',
          '[.theme-liquid-glass_&]:bg-white/10',
          isHorizontal ? 'flex-row p-2 gap-2 border-b-2 border-black' 
                       : 'flex-col p-2 gap-2 border-r-2 border-black',
          isHorizontal
            ? '[.theme-liquid-glass_&]:border-b [.theme-liquid-glass_&]:border-white/20'
            : '[.theme-liquid-glass_&]:border-r [.theme-liquid-glass_&]:border-white/20',
          tabListClassName
        )}
      >
        {tabs.map((tab, index) => {
          const isActive = tab.id === activeTab;
          const resolvedLabel = tab.labelKey ? t(tab.labelKey) : tab.label;
          const neobrutalistEffects = applyNeobrutalistEffectsToTabs && !tab.disabled && !isActive;

          return (
            <motion.button
              key={tab.id}
              ref={el => tabRefs.current[index] = el}
              id={`tab-${tab.id}`}
              role="tab"
              type="button"
              aria-selected={isActive}
              aria-controls={`panel-${tab.id}`}
              tabIndex={isActive ? 0 : -1} 
              disabled={tab.disabled}
              onClick={() => handleTabClick(tab.id)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={clsx(
                'relative font-semibold px-4 py-2.5 flex items-center justify-center gap-2 rounded-lg outline-none border-2',
                'transition-all duration-150 ease-out focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2',
                '[.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20',
                '[.theme-liquid-glass_&]:focus-visible:ring-violet-400 [.theme-liquid-glass_&]:focus-visible:ring-offset-slate-900',
                tabButtonClassName,
                isActive
                  ? clsx(
                      'bg-white text-emerald-600 border-black z-10',
                      '[.theme-liquid-glass_&]:bg-white/10 [.theme-liquid-glass_&]:text-white',
                      '[.theme-liquid-glass_&]:border-white/30',
                      activeTabButtonClassName
                    )
                  : clsx(
                      'bg-slate-100 text-slate-700 border-black',
                      '[.theme-liquid-glass_&]:bg-white/5 [.theme-liquid-glass_&]:text-white/70',
                      '[.theme-liquid-glass_&]:border-white/20',
                      tab.disabled ? 'opacity-60 cursor-not-allowed !shadow-none !transform-none' : 'cursor-pointer',
                      inactiveTabButtonClassName
                    )
              )}
              whileHover={neobrutalistEffects ? { x: -1, y: -1, boxShadow: '2px 2px 0px 0px rgba(0,0,0,1)'} : {}}
              whileTap={neobrutalistEffects ? { x: 0, y: 0, boxShadow: '1px 1px 0px 0px rgba(0,0,0,1)'} : {}}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              {tab.icon && <span className={clsx(resolvedLabel && "mr-1.5")}>{tab.icon}</span>}
              {resolvedLabel}
            </motion.button>
          );
        })}
      </div>

      {/* Tab Panel Container */}
      <div
        className={clsx(
            'relative overflow-hidden flex-grow bg-white',
            '[.theme-liquid-glass_&]:bg-white/5',
            tabPanelContainerClassName
        )}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab} 
            id={`panel-${activeTab}`}
            role="tabpanel"
            aria-labelledby={`tab-${activeTab}`}
            className={clsx(
                'p-5 md:p-6 h-full overflow-y-auto outline-none', 
                scrollbarClasses,
                contentClassName // Apply prop here
            )}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            tabIndex={0} 
          >
            {activeContent}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
